import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Upload as UploadIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Flight as FlightIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  LocalAirport as LocalAirportIcon,
  PendingActions as PendingActionsIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import dayjs from 'dayjs';
import {
  DetailDialog,
  RespondDialog,
  BaFailureDialog,
  BaUploadDialog,
  SignatureDialog
} from './CrewAssignments-part2';
import { formatDateTimeWithTimezone } from '../../utils/timezoneUtils';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * CrewAssignments
 *  - Shows the "My Assignments" table for crew
 *  - Handles BA upload / failure, ATC signature, techlog entry, and detail dialogs
 */
const CrewAssignments = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });
  const [linkingTlbSectorId, setLinkingTlbSectorId] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';

  // Dialog & form state (moved from CrewInbox)
  const [loading, setLoading] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [baDialogOpen, setBaDialogOpen] = useState(false);
  const [baFailureDialogOpen, setBaFailureDialogOpen] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [responseForm, setResponseForm] = useState({
    decision: 'ACCEPT',
    remarks: ''
  });
  const [baFailureForm, setBaFailureForm] = useState({
    remarks: ''
  });

  // Helper function to format route with validation
  const formatRoute = (assignment) => {
    const depAirport = assignment.departure?.airport || 'N/A';
    let arrAirport = assignment.arrival?.airport || 'N/A';

    // Validate arrival airport - check if it looks like a time (contains ":" and matches time pattern)
    if (arrAirport && /^\d{1,2}:\d{2}$/.test(arrAirport.trim())) {
      // This is likely a time value incorrectly stored in arrival.airport
      arrAirport = 'N/A (Invalid Data)';
    }

    return `${depAirport} → ${arrAirport}`;
  };

  // Helper function to build descriptive error messages
  const buildErrorMessage = (action, error) => {
    const errorDetails = error.response?.data;

    if (errorDetails?.errors && Array.isArray(errorDetails.errors) && errorDetails.errors.length > 0) {
      const validationErrors = errorDetails.errors
        .map((err) => {
          const field = err.param || err.path || 'field';
          const msg = err.msg || err.message || 'Invalid value';
          return `${field}: ${msg}`;
        })
        .join('; ');
      return `${action} failed - Validation errors: ${validationErrors}`;
    } else if (errorDetails?.message) {
      return `${action} failed: ${errorDetails.message}`;
    } else if (error.response?.status === 403) {
      return `${action} failed: You do not have permission to perform this action.`;
    } else if (error.response?.status === 401) {
      return `${action} failed: Your session has expired. Please log in again.`;
    } else if (error.response?.status >= 500) {
      return `${action} failed: Server error. Please try again later or contact support.`;
    } else {
      return `${action} failed: ${error.message || 'An unexpected error occurred'}`;
    }
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/crew/my-assignments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToAssignment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/crew/sectors/${selectedAssignment._id}/respond`,
        responseForm,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchAssignments();
      setResponseDialogOpen(false);
      resetResponseForm();

      // Show success message
      const sector = response.data;
      const decision = responseForm.decision === 'ACCEPT' ? 'accepted' : 'rejected';
      const charterNumber = sector.tripId?.charterNumber || 'N/A';
      const decisionMessage =
        responseForm.decision === 'ACCEPT'
          ? `Assignment accepted! Charter: ${charterNumber} | Sector ${sector.seqNo} | Status: ${
              sector.status || 'CREW_ACCEPTED'
            }. Operations has been notified.`
          : `Assignment rejected! Charter: ${charterNumber} | Sector ${sector.seqNo} | Status: ${
              sector.status || 'CREW_REJECTED'
            }. Operations has been notified for reassignment.`;
      showToast(decisionMessage, responseForm.decision === 'ACCEPT' ? 'success' : 'warning');
    } catch (error) {
      console.error('Failed to respond to assignment:', error);
      showToast(buildErrorMessage('Assignment response', error), 'error');
    }
  };

  const uploadBAVideo = async (file, baTestTime) => {
    try {
      if (!baTestTime) {
        showToast('BA test time is required', 'error');
        return;
      }
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('video', file);
      formData.append('baTestTime', baTestTime);

      const response = await axios.post(
        `/api/compliance/sectors/${selectedAssignment._id}/ba/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      await fetchAssignments();
      setBaDialogOpen(false);

      // Show success message
      const sector = response.data;
      const charterNumber = sector.tripId?.charterNumber || 'N/A';
      showToast(
        `BA test video uploaded successfully! Charter: ${charterNumber} | Sector ${
          sector.seqNo
        } | Status: ${sector.status || 'BA_PASSED'}. Operations has been notified.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to upload BA video:', error);
      showToast(buildErrorMessage('BA test video upload', error), 'error');
      throw error;
    }
  };

  const signClearance = async (signatureDataUrl) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/atc/sectors/${selectedAssignment._id}/clearance/sign`,
        {
          signatureData: signatureDataUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchAssignments();
      setSignDialogOpen(false);

      // Show success message
      const sector = response.data;
      const charterNumber = sector.tripId?.charterNumber || 'N/A';
      showToast(
        `ATC clearance signed successfully! Charter: ${charterNumber} | Sector ${
          sector.seqNo
        } | Status: ${sector.status || 'COMPLETED'}. Operations has been notified.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to sign clearance:', error);
      showToast(buildErrorMessage('ATC clearance signature', error), 'error');
    }
  };

  const resetResponseForm = () => {
    setResponseForm({ decision: 'ACCEPT', remarks: '' });
  };

  const openResponseDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setResponseDialogOpen(true);
  };

  const openBaDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setBaDialogOpen(true);
  };

  const openSignDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setSignDialogOpen(true);
  };

  const openBaFailureDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setBaFailureForm({ remarks: '' });
    setBaFailureDialogOpen(true);
  };

  const openDetailDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setDetailDialogOpen(true);
  };

  const enterTechlogForSector = async (assignment) => {
    const tripId = assignment.tripId?._id || assignment.tripId;
    if (!tripId) {
      showToast('Charter not found for this sector', 'error');
      return;
    }

    try {
      setLinkingTlbSectorId(assignment._id);
      const token = localStorage.getItem('token');
      const aircraftId = assignment.aircraftId?._id || assignment.aircraftId;

      // Opens charter TLB if needed, then places this sector on the correct sheet
      // (creates CHARTER_OVERFLOW when format sectorSlots is full).
      const linkRes = await axios.post(
        `/api/technical-log-book/open-for-trip/${tripId}/link-sector`,
        {
          sectorId: assignment._id,
          aircraftId: aircraftId || undefined,
          sectorNumber: assignment.seqNo || 1,
          departurePlace: assignment.departure?.airport || '',
          arrivalPlace: assignment.arrival?.airport || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const tlbId = linkRes.data?._id;
      if (!tlbId) {
        showToast('Could not open Technical Log Book for this charter', 'error');
        return;
      }

      await fetchAssignments();
      if (linkRes.data?.overflowCreated) {
        showToast(
          `Sector ${assignment.seqNo || ''} linked on a new techlog sheet (sector limit reached)`,
          'success'
        );
      }
      navigate('/technical-log-book', { state: { openEntryId: tlbId } });
    } catch (error) {
      console.error('Failed to enter techlog:', error);
      showToast(buildErrorMessage('Open Technical Log Book', error), 'error');
    } finally {
      setLinkingTlbSectorId(null);
    }
  };

  const submitBaFailure = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/api/crew/sectors/${selectedAssignment._id}/respond`,
        {
          status: 'BA_FAILED',
          remarks: baFailureForm.remarks
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchAssignments();
      setBaFailureDialogOpen(false);
      setSelectedAssignment(null);

      // Show success message
      const sector = response.data;
      const charterNumber = sector.tripId?.charterNumber || 'N/A';
      showToast(
        `BA test issue reported successfully! Charter: ${charterNumber} | Sector ${
          sector.seqNo
        } | Status: ${sector.status || 'BA_FAILED'}. Operations has been notified for review.`,
        'warning'
      );
    } catch (error) {
      console.error('Failed to report BA issue:', error);
      showToast(buildErrorMessage('BA test issue report', error), 'error');
    }
  };

  const toggleRowExpansion = (sectorId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [sectorId]: !prev[sectorId]
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      ASSIGNED: 'warning',
      CREW_ACCEPTED: 'success',
      CREW_REJECTED: 'error',
      READY_FOR_BA: 'info',
      READY_FOR_ATC: 'primary',
      CLEARED: 'secondary',
      COMPLETED: 'success',
      SECTOR_COMPLETED: 'success',
      CANCELLED: 'error',
      BA_FAILED: 'error',
      BA_PASSED: 'success',
      ATC_PASSED: 'success',
      ATC_FAILED: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusActions = (assignment) => {
    const actions = [];

    if (assignment.status === 'ASSIGNED') {
      actions.push(
        <Button
          key="accept"
          size="small"
          startIcon={<PendingActionsIcon />}
          color="warning"
          onClick={() => openResponseDialog(assignment)}
        >
          Respond
        </Button>
      );
    }

    if (assignment.status === 'READY_FOR_BA') {
      actions.push(
        <Button
          key="ba"
          size="small"
          startIcon={<UploadIcon />}
          color="primary"
          onClick={() => openBaDialog(assignment)}
        >
          Upload BA
        </Button>
      );
      actions.push(
        <Button
          key="ba-fail"
          size="small"
          color="error"
          onClick={() => openBaFailureDialog(assignment)}
        >
          Report Issue
        </Button>
      );
    }

    if (assignment.status === 'READY_FOR_ATC') {
      actions.push(
        <Button key="waiting" size="small" color="info" disabled>
          Waiting for ATC
        </Button>
      );
    }

    if (assignment.status === 'CLEARED') {
      actions.push(
        <Button
          key="sign"
          size="small"
          startIcon={<EditIcon />}
          color="secondary"
          onClick={() => openSignDialog(assignment)}
        >
          Sign
        </Button>
      );
    }

    if (assignment.status === 'COMPLETED' || assignment.status === 'SECTOR_COMPLETED') {
      actions.push(
        <Button
          key="techlog"
          size="small"
          startIcon={<MenuBookIcon />}
          color="primary"
          disabled={linkingTlbSectorId === assignment._id}
          onClick={() => enterTechlogForSector(assignment)}
        >
          Enter Techlog
        </Button>
      );
    }

    actions.push(
      <IconButton
        key="details"
        size="small"
        onClick={() => openDetailDialog(assignment)}
        color="primary"
      >
        <InfoIcon />
      </IconButton>
    );

    return actions;
  };

  const formatDateTime = (date, time) => {
    if (!date) return 'N/A';
    if (displayTimezone) {
      return formatDateTimeWithTimezone(date, time || null, displayTimezone);
    }
    const dateStr = dayjs(date).format('DD MMM YYYY');
    return time ? `${dateStr} ${time}` : dateStr;
  };

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            My Assignments
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px"></TableCell>
                  <TableCell>Sector</TableCell>
                  <TableCell>Charter</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Departure</TableCell>
                  <TableCell>Arrival</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => {
                  const trip = assignment.tripId;
                  const aircraft = assignment.aircraftId;
                  const isExpanded = expandedRows[assignment._id];

                  return (
                    <React.Fragment key={assignment._id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(assignment._id)}
                          >
                            <ExpandMoreIcon
                              sx={{
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }}
                            />
                          </IconButton>
                        </TableCell>
                        <TableCell>Sector {assignment.seqNo}</TableCell>
                        <TableCell>
                          {trip?.charterNumber || 'N/A'}
                          {trip?.title && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {trip.title}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatRoute(assignment)}</TableCell>
                        <TableCell>
                          {formatDateTime(
                            assignment.departure.date,
                            assignment.departure.time
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(
                            assignment.arrival.estimatedDate,
                            assignment.arrival.estimatedTime
                          )}
                        </TableCell>
                        <TableCell>
                          {aircraft
                            ? `${aircraft.registration} (${aircraft.manufacturer} ${aircraft.model})`
                            : 'Not assigned'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={assignment.status}
                            color={getStatusColor(assignment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {getStatusActions(assignment)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Grid container spacing={2}>
                                {/* Trip Information */}
                                <Grid item xs={12} md={6}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle1" gutterBottom>
                                        <FlightIcon
                                          sx={{ mr: 1, verticalAlign: 'middle' }}
                                        />
                                        Trip Information
                                      </Typography>
                                      <Divider sx={{ my: 1 }} />
                                      <List dense>
                                        <ListItem>
                                          <ListItemText
                                            primary="Charter Number"
                                            secondary={trip?.charterNumber || 'N/A'}
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Title"
                                            secondary={trip?.title || 'N/A'}
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Flight Type"
                                            secondary={trip?.flightType || 'N/A'}
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Wake Turbulence"
                                            secondary={assignment.wakeTurbulence || 'N/A'}
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Flight Rules"
                                            secondary={assignment.flightRules || 'N/A'}
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Client"
                                            secondary={
                                              trip?.clientName ||
                                              trip?.clientContact?.email ||
                                              'N/A'
                                            }
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Trip Status"
                                            secondary={
                                              <Chip
                                                label={trip?.status || 'N/A'}
                                                size="small"
                                                color={getStatusColor(trip?.status)}
                                              />
                                            }
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Compliance Requirements"
                                            secondary={
                                              <Box sx={{ mt: 0.5 }}>
                                                <Chip
                                                  label="BA Required"
                                                  size="small"
                                                  color="info"
                                                  sx={{ mr: 0.5 }}
                                                  title="Mandatory DGCA requirement"
                                                />
                                                <Chip
                                                  label="ATC Required"
                                                  size="small"
                                                  color="info"
                                                  title="Mandatory DGCA requirement"
                                                />
                                              </Box>
                                            }
                                          />
                                        </ListItem>
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* Aircraft & Crew */}
                                <Grid item xs={12} md={6}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle1" gutterBottom>
                                        <LocalAirportIcon
                                          sx={{ mr: 1, verticalAlign: 'middle' }}
                                        />
                                        Aircraft & Crew
                                      </Typography>
                                      <Divider sx={{ my: 1 }} />
                                      <List dense>
                                        <ListItem>
                                          <ListItemText
                                            primary="Aircraft"
                                            secondary={
                                              aircraft
                                                ? `${aircraft.registration} - ${aircraft.manufacturer} ${aircraft.model}`
                                                : 'Not assigned'
                                            }
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Assigned Pilots"
                                            secondary={
                                              assignment.assignedPilots?.length > 0
                                                ? assignment.assignedPilots
                                                    .map(
                                                      (p) =>
                                                        `${p.firstName} ${p.lastName} (${p.email})`
                                                    )
                                                    .join(', ')
                                                : 'No pilots assigned'
                                            }
                                          />
                                        </ListItem>
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* Flight Requirements */}
                                {(assignment.requirements || assignment.weatherMediaId || (assignment.tripKitMediaIds && assignment.tripKitMediaIds.length) || assignment.tripKitMediaId) && (
                                  <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle1" gutterBottom>
                                          <AssignmentIcon
                                            sx={{ mr: 1, verticalAlign: 'middle' }}
                                          />
                                          Flight Requirements
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <List dense>
                                          {((assignment.requirements?.fuelKg != null && assignment.requirements.fuelKg !== '') || (assignment.requirements?.fuelLitres != null && assignment.requirements.fuelLitres !== '') || assignment.requirements?.fuel) && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Planned Fuel Consumption"
                                                secondary={[
                                                  assignment.requirements.fuelKg != null && assignment.requirements.fuelKg !== '' ? `${assignment.requirements.fuelKg} kg` : null,
                                                  (assignment.requirements.fuelLitres != null && assignment.requirements.fuelLitres !== '') ? `${assignment.requirements.fuelLitres} L` : (assignment.requirements.fuel != null && assignment.requirements.fuel !== '' ? `${assignment.requirements.fuel} L` : null)
                                                ].filter(Boolean).join(', ')}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.requirements?.altitude && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Altitude"
                                                secondary={`${assignment.requirements.altitude} ft`}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.requirements?.route && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Route"
                                                secondary={assignment.requirements.route}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.requirements?.weather && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Weather"
                                                secondary={assignment.requirements.weather}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.weatherMediaId && assignment.weatherMediaId.fileUrl && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Weather Briefing File"
                                                secondary={
                                                  <Button
                                                    size="small"
                                                    component="a"
                                                    href={assignment.weatherMediaId.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    View / Download
                                                  </Button>
                                                }
                                              />
                                            </ListItem>
                                          )}
                                          {((assignment.tripKitMediaIds && assignment.tripKitMediaIds.length)
                                            ? assignment.tripKitMediaIds
                                            : (assignment.tripKitMediaId ? [assignment.tripKitMediaId] : [])
                                          ).map((media, idx) => {
                                            const url = typeof media === 'object' && media.fileUrl ? media.fileUrl : null;
                                            const label = (typeof media === 'object' && media.fileUrl) ? (media.fileUrl.split('/').pop() || `TripKit ${idx + 1}`) : `TripKit ${idx + 1}`;
                                            if (!url) return null;
                                            return (
                                              <ListItem key={media._id || idx}>
                                                <ListItemText
                                                  primary={label}
                                                  secondary={
                                                    <Button
                                                      size="small"
                                                      component="a"
                                                      href={url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                    >
                                                      View / Download
                                                    </Button>
                                                  }
                                                />
                                              </ListItem>
                                            );
                                          })}
                                        </List>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}

                                {/* Passengers */}
                                <Grid item xs={12} md={6}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle1" gutterBottom>
                                        <PeopleIcon
                                          sx={{ mr: 1, verticalAlign: 'middle' }}
                                        />
                                        Passengers & Weight
                                      </Typography>
                                      <Divider sx={{ my: 1 }} />
                                      <List dense>
                                        <ListItem>
                                          <ListItemText
                                            primary="Passenger Count"
                                            secondary={(() => {
                                              // Show sector passengers if available, otherwise show trip passengers as default
                                              const sectorPassengers =
                                                assignment.passengers?.length || 0;
                                              const tripPassengers =
                                                assignment.tripId?.passengers?.length || 0;
                                              return sectorPassengers > 0
                                                ? sectorPassengers
                                                : tripPassengers;
                                            })()}
                                          />
                                        </ListItem>
                                        <ListItem>
                                          <ListItemText
                                            primary="Total Weight"
                                            secondary={`${
                                              assignment.totalWeight ||
                                              assignment.tripId?.totalWeight ||
                                              0
                                            } kg`}
                                          />
                                        </ListItem>
                                        {((assignment.passengers &&
                                          assignment.passengers.length > 0) ||
                                          (assignment.tripId?.passengers &&
                                            assignment.tripId.passengers.length > 0)) && (
                                          <ListItem>
                                            <ListItemText
                                              primary="Passenger List"
                                              secondary={
                                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                  {(
                                                    assignment.passengers &&
                                                    assignment.passengers.length > 0
                                                      ? assignment.passengers
                                                      : assignment.tripId?.passengers || []
                                                  ).map((p, idx) => (
                                                    <li key={idx}>
                                                      {p.name} ({p.weight} kg)
                                                      {p.specialRequirements &&
                                                        ` - ${p.specialRequirements}`}
                                                    </li>
                                                  ))}
                                                </Box>
                                              }
                                            />
                                          </ListItem>
                                        )}
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* Remarks */}
                                {(assignment.remarks || assignment.clearanceRemarks) && (
                                  <Grid item xs={12}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle1" gutterBottom>
                                          Remarks
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <List dense>
                                          {assignment.remarks?.assignment && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Assignment Remarks"
                                                secondary={assignment.remarks.assignment}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.remarks?.requirements && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Requirements Remarks"
                                                secondary={assignment.remarks.requirements}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.remarks?.crewResponse && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Crew Response"
                                                secondary={assignment.remarks.crewResponse}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.remarks?.baTest && (
                                            <ListItem>
                                              <ListItemText
                                                primary="BA Test Remarks"
                                                secondary={assignment.remarks.baTest}
                                              />
                                            </ListItem>
                                          )}
                                          {(assignment.remarks?.clearance ||
                                            assignment.clearanceRemarks) && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Clearance Remarks"
                                                secondary={
                                                  assignment.remarks?.clearance ||
                                                  assignment.clearanceRemarks
                                                }
                                              />
                                            </ListItem>
                                          )}
                                        </List>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}

                                {/* ATC Clearance */}
                                {assignment.clearanceNumber && (
                                  <Grid item xs={12}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle1" gutterBottom>
                                          ATC Clearance
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <List dense>
                                          <ListItem>
                                            <ListItemText
                                              primary="Clearance Number"
                                              secondary={assignment.clearanceNumber}
                                            />
                                          </ListItem>
                                          {assignment.clearanceRequestedAt && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Requested At"
                                                secondary={formatDateTime(
                                                  assignment.clearanceRequestedAt,
                                                  null
                                                )}
                                              />
                                            </ListItem>
                                          )}
                                          {assignment.clearanceSentAt && (
                                            <ListItem>
                                              <ListItemText
                                                primary="Sent At"
                                                secondary={formatDateTime(
                                                  assignment.clearanceSentAt,
                                                  null
                                                )}
                                              />
                                            </ListItem>
                                          )}
                                        </List>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}

                                {/* Special Requirements */}
                                {(assignment.specialRequirements ||
                                  trip?.specialRequirements) && (
                                  <Grid item xs={12}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle1" gutterBottom>
                                          Special Requirements
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2">
                                          {assignment.specialRequirements ||
                                            trip?.specialRequirements}
                                        </Typography>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {assignments.length === 0 && !loading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No assignments found
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Dialogs - Extracted to CrewAssignments-part2.js */}
      <DetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        selectedAssignment={selectedAssignment}
        formatDateTime={formatDateTime}
        getStatusColor={getStatusColor}
      />

      <RespondDialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        selectedAssignment={selectedAssignment}
        responseForm={responseForm}
        setResponseForm={setResponseForm}
        onSubmit={respondToAssignment}
        formatRoute={formatRoute}
      />

      <BaFailureDialog
        open={baFailureDialogOpen}
        onClose={() => setBaFailureDialogOpen(false)}
        selectedAssignment={selectedAssignment}
        baFailureForm={baFailureForm}
        setBaFailureForm={setBaFailureForm}
        onSubmit={submitBaFailure}
        formatRoute={formatRoute}
      />

      <BaUploadDialog
        open={baDialogOpen}
        onClose={() => setBaDialogOpen(false)}
        selectedAssignment={selectedAssignment}
        formatRoute={formatRoute}
        onUpload={uploadBAVideo}
      />

      <SignatureDialog
        open={signDialogOpen}
        onClose={() => setSignDialogOpen(false)}
        selectedAssignment={selectedAssignment}
        formatRoute={formatRoute}
        onSign={signClearance}
      />
    </>
  );
};

export default CrewAssignments;


